import { useListState } from "@/hooks/use-list-state";
// Stats tab for /app/members/booking-requests — kept as a sibling file because
// the host page is near the enforced pages/** size cap. All aggregation is
// server-side (GET /api/app/booking-requests/stats) so this tab, the
// portfolio-analytics card, and the owner portal agree on the numbers.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PropertyMultiSelectFilter } from "@/components/property-multi-select-filter";
import { DateRangeFilter, computeDateRange, type DatePreset } from "@/components/DateRangeFilter";
import { BookingStatusPie, BookingStatsTimeChart, BOOKING_STATUS_CHART_COLORS } from "@/components/booking-stats-charts";
import type { BookingStatsResponse, BookingStatsGroupBy } from "@shared/booking-stats";
import type { BookingRequestTemplate, BookingRequestStatus } from "@shared/schema";
import { Sparkles } from "lucide-react";

type Property = { id: number; name?: string | null; address?: string | null; status?: string | null; propertyGroup?: string | null };

const STATS_PRESETS: readonly DatePreset[] = [
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "this_month",
  "last_month",
  "last_quarter",
  "last_year",
  "custom",
];

export interface StatsDrillThrough {
  status?: BookingRequestStatus;
  propertyIds?: number[];
  templateId?: number | null;
  /** Submissions-tab date filter equivalent of the current stats range, so the
   * drilled list matches the chart counts. "all" when no exact mapping exists. */
  dateRange?: "all" | "7d" | "30d" | "90d" | "last_month" | "last_quarter" | "last_year";
}

/** Map a stats preset onto the Submissions tab's date-filter options. */
function drillDateRange(preset: DatePreset): StatsDrillThrough["dateRange"] {
  switch (preset) {
    case "last_7_days": return "7d";
    case "last_30_days": return "30d";
    case "last_90_days": return "90d";
    case "last_month": return "last_month";
    case "last_quarter": return "last_quarter";
    case "last_year": return "last_year";
    default: return "all"; // this_month / custom have no Submissions equivalent — superset
  }
}

function statsQueryString(params: {
  from: string; to: string; propertyIds: number[]; templateId: string; groupBy?: BookingStatsGroupBy;
}): string {
  const qs = new URLSearchParams({ from: params.from, to: params.to });
  if (params.propertyIds.length > 0) qs.set("propertyIds", params.propertyIds.join(","));
  if (params.templateId !== "all") qs.set("templateId", params.templateId);
  if (params.groupBy) qs.set("groupBy", params.groupBy);
  return qs.toString();
}

function KpiCard({ label, value, sub, testId }: { label: string; value: string; sub?: string; testId: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold mt-1" data-testid={testId}>{value}</div>
      {sub ? <div className="text-xs text-muted-foreground mt-0.5">{sub}</div> : null}
    </Card>
  );
}

export function BookingRequestsStatsTab({ templates, properties, onDrillThrough }: {
  templates: BookingRequestTemplate[];
  properties: Property[];
  onDrillThrough: (filters: StatsDrillThrough) => void;
}) {
  const [preset, setPreset] = useListState<DatePreset>("booking-stats:preset", "last_30_days");
  const [customFrom, setCustomFrom] = useListState("booking-stats:from", "");
  const [customTo, setCustomTo] = useListState("booking-stats:to", "");
  const [propertyIds, setPropertyIds] = useListState<number[]>("booking-stats:properties", []);
  const [templateId, setTemplateId] = useListState<string>("booking-stats:template", "all");
  const [groupBy, setGroupBy] = useListState<BookingStatsGroupBy>("booking-stats:group", "status");

  const range = useMemo(() => computeDateRange(preset, customFrom, customTo), [preset, customFrom, customTo]);
  const rangeReady = Boolean(range.from && range.to);

  const statsQuery = useQuery<BookingStatsResponse>({
    queryKey: ["/api/app/booking-requests/stats", range.from, range.to, propertyIds, templateId, groupBy],
    queryFn: async () => {
      const res = await fetch(`/api/app/booking-requests/stats?${statsQueryString({ from: range.from, to: range.to, propertyIds, templateId, groupBy })}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load stats");
      return res.json();
    },
    enabled: rangeReady,
  });

  const aiQuery = useQuery<{ summary: string; source: "ai" | "fallback" }>({
    queryKey: ["/api/app/booking-requests/stats/ai-summary", range.from, range.to, propertyIds, templateId],
    queryFn: async () => {
      const res = await fetch(`/api/app/booking-requests/stats/ai-summary?${statsQueryString({ from: range.from, to: range.to, propertyIds, templateId })}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load summary");
      return res.json();
    },
    enabled: rangeReady,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const stats = statsQuery.data;
  const totals = stats?.totals;
  const drillBase: StatsDrillThrough = {
    propertyIds,
    templateId: templateId === "all" ? null : Number(templateId),
    dateRange: drillDateRange(preset),
  };
  const GRANULARITY_LABEL = { day: "daily", week: "weekly", month: "monthly" } as const;

  return (
    <div className="space-y-4" data-testid="booking-stats-tab">
      {/* Filters — same dimensions as the Submissions tab, minus status (the
          charts ARE the status breakdown). */}
      <div className="flex flex-wrap gap-2 items-center">
        <DateRangeFilter
          preset={preset}
          customFrom={customFrom}
          customTo={customTo}
          onPresetChange={(p) => setPreset(p || "last_30_days")}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          presets={STATS_PRESETS}
          allowAll={false}
          triggerClassName="h-9 w-auto min-w-[150px] text-sm"
        />
        <PropertyMultiSelectFilter
          properties={properties as any}
          value={propertyIds}
          onChange={setPropertyIds}
          className="min-w-[180px]"
          noPropertyOption
        />
        <Select value={templateId} onValueChange={setTemplateId}>
          <SelectTrigger className="h-9 w-auto min-w-[180px] text-sm" data-testid="select-stats-template-filter">
            <SelectValue placeholder="All templates" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All templates</SelectItem>
            {templates.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={groupBy} onValueChange={(v) => setGroupBy(v as BookingStatsGroupBy)}>
          <SelectTrigger className="h-9 w-auto min-w-[150px] text-sm" data-testid="select-stats-group-by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="status">Chart by status</SelectItem>
            <SelectItem value="property">Chart by property</SelectItem>
            <SelectItem value="template">Chart by template</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {statsQuery.isError ? (
        <Card className="p-8 text-center space-y-3" data-testid="booking-stats-error">
          <p className="text-sm text-muted-foreground">Couldn't load booking stats for this period.</p>
          <Button variant="outline" size="sm" onClick={() => statsQuery.refetch()} data-testid="button-stats-retry">
            Try again
          </Button>
        </Card>
      ) : statsQuery.isLoading || !stats ? (
        <div className="space-y-3" data-testid="booking-stats-loading">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      ) : (
        <>
          {/* KPI row. Pending is excluded on purpose: decisions land within
              48h, so the stats read on outcomes, not queue state. */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard label="Decided" value={String(totals!.decided)} sub="approved + rejected + archived" testId="kpi-decided" />
            <KpiCard
              label="Approval rate"
              value={totals!.approvalRate == null ? "—" : `${Math.round(totals!.approvalRate * 100)}%`}
              sub={`${totals!.approved} approved · ${totals!.rejected} rejected`}
              testId="kpi-approval-rate"
            />
            <KpiCard
              label="Median time to decision"
              value={totals!.medianDaysToDecision == null ? "—" : `${totals!.medianDaysToDecision}d`}
              sub="submission → decision"
              testId="kpi-median-days"
            />
            <KpiCard label="Archived" value={String(totals!.archived)} sub="applicant withdrew" testId="kpi-archived" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card className="p-4">
              <div className="text-sm font-medium mb-1">Decisions by status</div>
              <p className="text-xs text-muted-foreground mb-3">Current status of submissions received in this period. Click a status to see those submissions.</p>
              {totals!.decided === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="booking-stats-empty">No booking decisions in this period.</p>
              ) : (
                <div>
                  <BookingStatusPie byStatus={stats.byStatus} decided={totals!.decided} />
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {stats.byStatus.filter((s) => s.count > 0).map((s) => (
                      <button
                        key={s.status}
                        type="button"
                        className="text-xs px-2 py-1 rounded border hover:bg-muted"
                        onClick={() => onDrillThrough({ ...drillBase, status: s.status })}
                        data-testid={`button-drill-${s.status}`}
                      >
                        View {s.status} →
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card className="p-4">
              <div className="text-sm font-medium mb-1">Rejection reasons</div>
              <p className="text-xs text-muted-foreground mb-3">Structured categories chosen at reject time. Older rejections without a category show as Uncategorized.</p>
              {stats.byRejectionCategory.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="rejection-categories-empty">No rejections in this period.</p>
              ) : (
                <div className="space-y-2" data-testid="rejection-categories">
                  {stats.byRejectionCategory.map((c) => {
                    const max = stats.byRejectionCategory[0]?.count || 1;
                    return (
                      <div key={c.category} className="flex items-center gap-2 text-sm">
                        <span className="w-40 shrink-0 truncate text-muted-foreground" title={c.label}>{c.label}</span>
                        <div className="flex-1 h-3 rounded bg-muted overflow-hidden">
                          <div
                            className="h-full rounded"
                            style={{ width: `${Math.max(6, Math.round((c.count / max) * 100))}%`, backgroundColor: BOOKING_STATUS_CHART_COLORS.rejected }}
                          />
                        </div>
                        <span className="w-6 text-right font-medium">{c.count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          <Card className="p-4">
            <div className="text-sm font-medium mb-1">Decisions over time</div>
            <p className="text-xs text-muted-foreground mb-3">Submissions bucketed by the date they were received ({GRANULARITY_LABEL[stats.timeSeries.granularity]}).</p>
            {totals!.decided === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Nothing to chart for this period.</p>
            ) : (
              <BookingStatsTimeChart timeSeries={stats.timeSeries} />
            )}
          </Card>

          <Card className="p-4" data-testid="booking-stats-ai-card">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-4 w-4 text-brand" />
              <div className="text-sm font-medium">Rejection summary</div>
            </div>
            {aiQuery.isLoading ? (
              <div className="space-y-2 mt-2" data-testid="booking-stats-ai-loading">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : aiQuery.data ? (
              <div>
                <p className="text-sm whitespace-pre-wrap" data-testid="text-booking-stats-ai-summary">{aiQuery.data.summary}</p>
                {aiQuery.data.source === "fallback" && (
                  <p className="text-xs text-muted-foreground mt-2" data-testid="text-ai-fallback-note">Quick summary — AI analysis unavailable right now.</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="text-ai-summary-error">Could not load the summary for this period.</p>
            )}
            <p className="text-[11px] text-muted-foreground mt-3">Internal use only — summarizes your team's rejection notes for this period. Not for sharing with owners or applicants.</p>
          </Card>
        </>
      )}
    </div>
  );
}
